import { computed, Injectable, signal } from '@angular/core';
import { safeJsonParse } from '@core/utils/safe-json-parse';
import { IARA_LAUNCHER_DEFAULT_POSITION } from '../constants/iara-launcher-positions';
import type {
  IaraLauncherPosition,
  IaraPanelPreference,
} from '../models/iara-launcher-position.model';
import { isIaraLauncherPosition } from '../utils/iara-launcher-position';

export const IARA_PANEL_STORAGE_KEY = 'FLUX_CONTROL.IARA_PANEL';

/**
 * Preferências do painel flutuante: se estava aberto e onde o gatilho fica. Segue a
 * forma do `ColorModeService` — signal privado com estado inicial lido do storage,
 * `asReadonly()` para fora, e leitura sempre validada.
 *
 * Os dois campos moram na MESMA chave por serem a mesma preferência do mesmo widget,
 * lida pelos mesmos dois componentes. Isso obriga a ter UM `persist()` que serializa os
 * dois: gravar `{ open }` e `{ position }` em métodos separados fazia cada escrita
 * apagar o campo do outro.
 */
@Injectable({ providedIn: 'root' })
export class IaraPanelStateService {
  private readonly storage =
    typeof globalThis !== 'undefined' && 'localStorage' in globalThis
      ? globalThis.localStorage
      : null;

  private readonly preference = signal(this.readInitial());

  /**
   * Conversa que o painel está EXIBINDO agora. Diferente de `isOpen`: não é
   * preferência, não vai para o storage e vale `null` quando o painel está fechado
   * ou nem montado (em `/chat`, por exemplo, onde o painel não existe embora a
   * preferência salva possa dizer "aberto").
   */
  private readonly visibleSession = signal<string | null>(null);

  /**
   * Posição em pré-visualização durante o arraste. Transitória como `visibleSession`:
   * não persiste, senão cada passo do arraste escreveria no storage.
   */
  private readonly pendingPosition = signal<IaraLauncherPosition | null>(null);

  public readonly isOpen = computed(() => this.preference().open);

  public readonly position = computed(() => this.preference().position);

  /** Posição a RENDERIZAR: a do arraste em curso, se houver, senão a persistida. */
  public readonly effectivePosition = computed(() => this.pendingPosition() ?? this.position());

  public toggle(): void {
    this.setOpen(!this.isOpen());
  }

  public close(): void {
    this.setOpen(false);
  }

  /** Comita a posição e persiste, encerrando qualquer pré-visualização. */
  public moveTo(position: IaraLauncherPosition): void {
    if (!isIaraLauncherPosition(position)) return;

    this.pendingPosition.set(null);
    this.preference.update((current) => ({ ...current, position }));
    this.persist();
  }

  /** Pré-visualiza durante o arraste; `null` descarta e volta à posição persistida. */
  public previewMove(position: IaraLauncherPosition | null): void {
    this.pendingPosition.set(
      position !== null && isIaraLauncherPosition(position) ? position : null,
    );
  }

  /** Chamado pelo painel; `null` quando ele fecha ou é destruído. */
  public publishVisibleSession(sessionId: string | null): void {
    this.visibleSession.set(sessionId);
  }

  /**
   * Esta conversa está na frente do usuário neste momento? Quem avisa "a Iara
   * terminou" usa isto para não notificar alguém que está vendo a resposta chegar.
   */
  public isSessionVisible(sessionId: string): boolean {
    return this.visibleSession() === sessionId;
  }

  private setOpen(open: boolean): void {
    this.preference.update((current) => ({ ...current, open }));
    this.persist();
  }

  /** Único ponto de escrita: serializa os DOIS campos, sempre. */
  private persist(): void {
    this.storage?.setItem(IARA_PANEL_STORAGE_KEY, JSON.stringify(this.preference()));
  }

  /**
   * Nunca confia no storage. Campo ausente, tipo errado, JSON corrompido ou nome de
   * posição que não existe mais caem no default — em vez de propagar lixo para o layout.
   */
  private readInitial(): IaraPanelPreference {
    const raw = this.storage?.getItem(IARA_PANEL_STORAGE_KEY);
    const parsed = raw ? safeJsonParse<Partial<IaraPanelPreference>>(raw) : null;

    return {
      open: parsed?.open === true,
      position: isIaraLauncherPosition(parsed?.position)
        ? parsed.position
        : IARA_LAUNCHER_DEFAULT_POSITION,
    };
  }
}
